import React, { useState } from 'react';

import { postJSON } from '../../api/client.js';
import {
  CrudPage, FeaturesPage, HelpIndexPage, HookTester, OrgSettingsForm,
  UniActionButton, UniKpiCard, UniPageShell, UniSection, UniTable, UniTile,
  fmtCount, useUniList,
} from '../waba/WabaShared.jsx';

export const RAG_API = '/api/rag';
const THEME = 'rag';
const HOME = '/rag';
const P = { theme: THEME, api: RAG_API, home: HOME };

export function RagDashboard() {
  const d = useUniList(`${RAG_API}/dashboard/`);
  const s = d.raw || {};
  const v = (n) => (d.loading ? '…' : fmtCount(n));
  return (
    <UniPageShell
      theme={THEME}
      icon="🧠"
      title="RAG knowledge & AI"
      subtitle="Retrieval-augmented answers for WhatsApp and the test chat. Per-organisation schema."
      helpPage="dashboard"
      helpApi={RAG_API}
      actions={(
        <>
          <UniActionButton hero primary to="/rag/test-chat">Test chat</UniActionButton>
          <UniActionButton hero to="/rag/knowledge">Knowledge</UniActionButton>
          <UniActionButton hero to="/rag/training">Train</UniActionButton>
        </>
      )}
    >
      <UniSection title="At a glance" />
      <div className="uni-kpis">
        <UniKpiCard icon="🗂" label="Systems" value={v(s.systems)} to="/rag/systems" />
        <UniKpiCard icon="📄" label="Documents" value={v(s.documents)} to="/rag/knowledge" />
        <UniKpiCard icon="✅" label="Indexed" value={v(s.indexed)} to="/rag/training" />
        <UniKpiCard icon="💬" label="Conversations" value={v(s.conversations)} to="/rag/conversations" />
        <UniKpiCard icon="⚠️" label="Open issues" value={v(s.open_issues)} to="/rag/quality" />
        <UniKpiCard icon="🛍" label="Products" value={v(s.products)} to="/rag/products" />
      </div>
      <UniSection title="Open" />
      <div className="uni-tiles">
        <UniTile to="/rag/knowledge" icon="📄" title="Knowledge" sub="Documents, versions, search" />
        <UniTile to="/rag/categories" icon="📁" title="Categories" sub="Folders for retrieval" />
        <UniTile to="/rag/training" icon="🏋️" title="Training" sub="Chunk, embed, index" />
        <UniTile to="/rag/quality" icon="🎯" title="Quality" sub="Hallucinations and gaps" />
        <UniTile to="/rag/faq" icon="❓" title="FAQs" sub="Frequent questions" />
        <UniTile to="/rag/chats" icon="💬" title="Chat analytics" sub="Every AI turn" />
        <UniTile to="/rag/conversations" icon="🎧" title="Takeover hub" sub="Pause AI, reply as human" />
        <UniTile to="/rag/customers" icon="👤" title="Customers" sub="Scores and tags" />
        <UniTile to="/rag/sales" icon="💰" title="Sales" sub="Funnel and UTM" />
        <UniTile to="/rag/followups" icon="⏰" title="Follow-ups" sub="Sequences over WhatsApp" />
        <UniTile to="/rag/escalation" icon="🚨" title="Escalation" sub="Low confidence rules" />
        <UniTile to="/rag/reports" icon="📊" title="Reports" sub="Daily / quality / sales" />
        <UniTile to="/rag/systems" icon="🧩" title="Systems" sub="Named RAG personas" />
        <UniTile to="/rag/products" icon="🛍" title="Products" sub="Catalog in the prompt" />
        <UniTile to="/rag/playground" icon="🧪" title="Playground" sub="Try prompts safely" />
        <UniTile to="/rag/config" icon="⚙️" title="Config" sub="Org AWS, LLM and embeddings" />
        <UniTile to="/rag/hooks" icon="🔌" title="Hooks" sub="Shop / CRM / WhatsApp" />
        <UniTile to="/rag/automation" icon="⏱" title="Automation" sub="Follow-up and report crons" />
        <UniTile to="/rag/help" icon="📘" title="Help" sub="Same copy on mobile" />
        <UniTile to="/rag/features" icon="✨" title="Features" sub="Full capability list" />
      </div>
    </UniPageShell>
  );
}

export function RagSystems() {
  return (
    <CrudPage
      {...P} helpPage="systems" icon="🧩" title="RAG systems"
      endpoint={`${RAG_API}/systems/`}
      createLabel="+ System"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'slug', label: 'Slug', required: true },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'icon', label: 'Icon' },
        { key: 'is_active', label: 'Active', type: 'check' },
      ]}
    />
  );
}

export function RagConfig() {
  return (
    <OrgSettingsForm
      {...P}
      helpPage="config"
      icon="⚙️"
      title="RAG configuration"
      subtitle="AWS, LLM and embeddings for this organisation. Not read from settings.py."
      endpoint={`${RAG_API}/org-settings/`}
      extraActions={(
        <>
          <UniActionButton hero to="/rag/config/keys">Advanced keys</UniActionButton>
          <UniActionButton hero to="/rag/costs">Costs</UniActionButton>
        </>
      )}
    />
  );
}

export function RagConfigKeys() {
  return (
    <CrudPage
      {...P} helpPage="config_keys" icon="🔑" title="Advanced RAG keys"
      endpoint={`${RAG_API}/config/`}
      searchPlaceholder="Search keys…"
      createLabel="+ Setting"
      extraActions={<UniActionButton hero to="/rag/config">← Configuration</UniActionButton>}
      fields={[
        { key: 'key', label: 'Key', required: true },
        { key: 'value', label: 'Value', type: 'textarea' },
        { key: 'value_type', label: 'Type' },
        { key: 'category', label: 'Category' },
        { key: 'system', label: 'System id (optional)' },
      ]}
    />
  );
}

export function RagCategories() {
  return (
    <CrudPage
      {...P} helpPage="categories" icon="📁" title="Categories"
      endpoint={`${RAG_API}/categories/`}
      createLabel="+ Category"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'slug', label: 'Slug' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]}
    />
  );
}

export function RagKnowledge() {
  const trainAll = async () => {
    await postJSON(`${RAG_API}/knowledge/train_all/`, { job_type: 'incremental' });
    window.location.href = '/rag/training';
  };
  return (
    <CrudPage
      {...P} helpPage="knowledge" icon="📄" title="Knowledge base"
      endpoint={`${RAG_API}/knowledge/`}
      searchPlaceholder="Search documents…"
      createLabel="+ Document"
      extraActions={<UniActionButton hero onClick={trainAll}>Train all</UniActionButton>}
      fields={[
        { key: 'title', label: 'Title', required: true },
        { key: 'content', label: 'Body', type: 'textarea', rows: 10, required: true },
        { key: 'category', label: 'Category id', required: true },
        { key: 'system', label: 'System id' },
        { key: 'status', label: 'Status', type: 'select', options: [
          { value: 'draft', label: 'Draft' },
          { value: 'active', label: 'Active' },
          { value: 'archived', label: 'Archived' },
        ] },
        { key: 'audience', label: 'Audience', type: 'select', options: [
          { value: 'both', label: 'Both' },
          { value: 'customer', label: 'Customer' },
          { value: 'distributor', label: 'Distributor' },
        ] },
        { key: 'language', label: 'Language' },
      ]}
    />
  );
}

export function RagVersions() {
  return (
    <CrudPage
      {...P} helpPage="knowledge_edit" icon="🕘" title="Document versions"
      endpoint={`${RAG_API}/versions/`}
      readOnly
      fields={[
        { key: 'title', label: 'Title' },
        { key: 'content', label: 'Content', type: 'textarea' },
      ]}
    />
  );
}

export function RagChunks() {
  return (
    <CrudPage
      {...P} helpPage="training" icon="🧩" title="Chunks"
      endpoint={`${RAG_API}/chunks/`}
      readOnly
      fields={[
        { key: 'content', label: 'Content', type: 'textarea' },
        { key: 'chunk_index', label: 'Index', type: 'number' },
      ]}
    />
  );
}

export function RagTraining() {
  const jobs = useUniList(`${RAG_API}/training-jobs/`);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const run = async (job_type) => {
    setBusy(true);
    setMsg(null);
    try {
      const data = await postJSON(`${RAG_API}/knowledge/train_all/`, { job_type });
      setMsg(JSON.stringify(data));
      jobs.reload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <UniPageShell
      {...P} icon="🏋️" title="Training" helpPage="training"
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button type="button" className="ty-btn" disabled={busy} onClick={() => run('incremental')}>Incremental train</button>
        <button type="button" className="ty-btn ty-btn--secondary" disabled={busy} onClick={() => run('full')}>Full reindex</button>
      </div>
      {msg && <pre className="uni-note" style={{ whiteSpace: 'pre-wrap' }}>{msg}</pre>}
      <UniTable
        rows={jobs.rows} loading={jobs.loading} error={jobs.error} rowKey="id"
        empty="No training jobs yet"
        columns={[
          { key: 'id', label: 'Job' },
          { key: 'job_type', label: 'Type' },
          { key: 'status', label: 'Status' },
        ]}
      />
    </UniPageShell>
  );
}

export function RagQuality() {
  return (
    <CrudPage
      {...P} helpPage="quality" icon="🎯" title="Quality issues"
      endpoint={`${RAG_API}/quality/`}
      readOnly
      fields={[
        { key: 'issue_type', label: 'Type' },
        { key: 'status', label: 'Status' },
        { key: 'resolution_notes', label: 'Notes', type: 'textarea' },
      ]}
    />
  );
}

export function RagFaq() {
  return (
    <CrudPage
      {...P} helpPage="faq" icon="❓" title="FAQs"
      endpoint={`${RAG_API}/faqs/`}
      searchPlaceholder="Search questions…"
      createLabel="+ FAQ"
      fields={[
        { key: 'question_text', label: 'Question', required: true, type: 'textarea' },
        { key: 'best_response', label: 'Best answer', type: 'textarea' },
      ]}
    />
  );
}

export function RagChats() {
  return (
    <CrudPage
      {...P} helpPage="chats" icon="💬" title="Chat messages"
      endpoint={`${RAG_API}/messages/`}
      readOnly
      fields={[
        { key: 'content', label: 'Content', type: 'textarea' },
        { key: 'role', label: 'Role' },
      ]}
    />
  );
}

export function RagConversations() {
  const { rows, loading, error, reload } = useUniList(`${RAG_API}/conversations/`);
  const act = async (id, name) => {
    await postJSON(`${RAG_API}/conversations/${id}/${name}/`, {});
    reload();
  };
  return (
    <UniPageShell
      {...P} icon="🎧" title="Conversation hub" helpPage="conversations"
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      <UniTable
        rows={rows} loading={loading} error={error} rowKey="id"
        empty="No conversations"
        columns={[
          { key: 'phone_number', label: 'Phone' },
          { key: 'user_name', label: 'Name' },
          {
            key: 'actions',
            label: 'Actions',
            sortable: false,
            render: (c) => (
              <>
                <button type="button" className="uni-row-btn" onClick={() => act(c.id, 'takeover')}>Take over</button>
                <button type="button" className="uni-row-btn" onClick={() => act(c.id, 'resume-ai')}>Resume AI</button>
                <button type="button" className="uni-row-btn" onClick={() => act(c.id, 'resolve')}>Resolve</button>
              </>
            ),
          },
        ]}
      />
    </UniPageShell>
  );
}

export function RagCustomers() {
  return (
    <CrudPage
      {...P} helpPage="customers" icon="👤" title="Customers"
      endpoint={`${RAG_API}/customers/`}
      searchPlaceholder="Search phone…"
      fields={[
        { key: 'phone_number', label: 'Phone', required: true },
        { key: 'name', label: 'Name' },
        { key: 'tags', label: 'Tags JSON', type: 'json' },
      ]}
    />
  );
}

export function RagSales() {
  return (
    <CrudPage
      {...P} helpPage="sales" icon="💰" title="Sales tracking"
      endpoint={`${RAG_API}/sales/`}
      fields={[
        { key: 'sales_status', label: 'Status' },
        { key: 'utm_source', label: 'UTM source' },
      ]}
    />
  );
}

export function RagEscalation() {
  return (
    <CrudPage
      {...P} helpPage="escalation" icon="🚨" title="Escalation rules"
      endpoint={`${RAG_API}/escalation/`}
      createLabel="+ Rule"
      fields={[
        { key: 'trigger_type', label: 'Trigger', required: true },
        { key: 'is_active', label: 'Active', type: 'check' },
      ]}
    />
  );
}

export function RagFollowups() {
  const processNow = async () => {
    await postJSON(`${RAG_API}/followups/process-now/`, {});
  };
  return (
    <CrudPage
      {...P} helpPage="followups" icon="⏰" title="Scheduled follow-ups"
      endpoint={`${RAG_API}/followups/`}
      createLabel="+ Follow-up"
      extraActions={(
        <>
          <UniActionButton hero onClick={processNow}>Process now</UniActionButton>
          <UniActionButton hero to="/rag/sequences">Sequences</UniActionButton>
          <UniActionButton hero to="/rag/trackers">Trackers</UniActionButton>
        </>
      )}
      fields={[
        { key: 'status', label: 'Status' },
        { key: 'message_content', label: 'Message', type: 'textarea' },
      ]}
    />
  );
}

export function RagSequences() {
  return (
    <CrudPage
      {...P} helpPage="followups" icon="🧵" title="Follow-up sequences"
      endpoint={`${RAG_API}/sequences/`}
      createLabel="+ Sequence"
      extraActions={<UniActionButton hero to="/rag/sequence-steps">Steps</UniActionButton>}
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'is_active', label: 'Active', type: 'check' },
      ]}
    />
  );
}

export function RagSequenceSteps() {
  return (
    <CrudPage
      {...P} helpPage="followups" icon="🧵" title="Sequence steps"
      endpoint={`${RAG_API}/sequence-steps/`}
      createLabel="+ Step"
      fields={[
        { key: 'sequence', label: 'Sequence id', required: true },
        { key: 'delay_minutes', label: 'Delay minutes', type: 'number' },
        { key: 'message_content', label: 'Message', type: 'textarea' },
      ]}
    />
  );
}

export function RagTrackers() {
  return (
    <CrudPage
      {...P} helpPage="followups" icon="📍" title="Follow-up trackers"
      endpoint={`${RAG_API}/trackers/`}
      readOnly
      fields={[{ key: 'status', label: 'Status' }]}
    />
  );
}

export function RagReports() {
  return (
    <CrudPage
      {...P} helpPage="reports" icon="📊" title="Reports"
      endpoint={`${RAG_API}/reports/`}
      createLabel="+ Report"
      extraActions={<UniActionButton hero to="/rag/health-reports">Health reports</UniActionButton>}
      fields={[
        { key: 'report_type', label: 'Type' },
        { key: 'title', label: 'Title' },
      ]}
    />
  );
}

export function RagHealthReports() {
  return (
    <CrudPage
      {...P} helpPage="reports" icon="🩺" title="Health reports"
      endpoint={`${RAG_API}/health-reports/`}
      readOnly
      fields={[{ key: 'phone_number', label: 'Phone' }]}
    />
  );
}

export function RagProducts() {
  return (
    <CrudPage
      {...P} helpPage="products" icon="🛍" title="Product catalogue"
      endpoint={`${RAG_API}/products/`}
      searchPlaceholder="Search products…"
      createLabel="+ Product"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'slug', label: 'Slug', required: true },
        { key: 'short_description', label: 'Short description', type: 'textarea' },
        { key: 'tagline', label: 'Tagline' },
        { key: 'is_active', label: 'Active', type: 'check' },
      ]}
    />
  );
}

export function RagPlayground() {
  const [query, setQuery] = useState('');
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      setOut(await postJSON(`${RAG_API}/playground/`, { query }));
    } catch (e) {
      setOut({ error: e.message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <UniPageShell
      {...P} icon="🧪" title="Prompt playground" helpPage="playground"
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      <textarea rows={4} value={query} onChange={(e) => setQuery(e.target.value)} style={{ width: '100%' }} />
      <button type="button" className="ty-btn" style={{ marginTop: 8 }} disabled={busy || !query} onClick={run}>
        {busy ? 'Running…' : 'Ask'}
      </button>
      {out && <pre className="uni-note" style={{ marginTop: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(out, null, 2)}</pre>}
    </UniPageShell>
  );
}

export function RagCosts() {
  return (
    <CrudPage
      {...P} helpPage="costs" icon="💳" title="API usage / costs"
      endpoint={`${RAG_API}/usage/`}
      readOnly
      fields={[
        { key: 'service', label: 'Service' },
        { key: 'model_id', label: 'Model' },
      ]}
    />
  );
}

export function RagAudit() {
  return (
    <CrudPage
      {...P} helpPage="audit" icon="📜" title="Audit log"
      endpoint={`${RAG_API}/audit/`}
      readOnly
      fields={[
        { key: 'action', label: 'Action' },
        { key: 'entity_title', label: 'Entity' },
      ]}
    />
  );
}

export function RagAlerts() {
  return (
    <CrudPage
      {...P} helpPage="alerts" icon="🔔" title="Alerts"
      endpoint={`${RAG_API}/alerts/`}
      readOnly
      fields={[
        { key: 'title', label: 'Title' },
        { key: 'message', label: 'Message', type: 'textarea' },
        { key: 'is_read', label: 'Read', type: 'check' },
      ]}
    />
  );
}

export function RagCompliance() {
  return (
    <CrudPage
      {...P} helpPage="compliance" icon="🛡" title="Compliance keywords"
      endpoint={`${RAG_API}/compliance/`}
      createLabel="+ Keyword"
      fields={[
        { key: 'keyword', label: 'Keyword', required: true },
        { key: 'severity', label: 'Severity' },
        { key: 'replacement', label: 'Suggested replacement' },
      ]}
    />
  );
}

export function RagResponseTemplates() {
  return (
    <CrudPage
      {...P} helpPage="templates" icon="💬" title="Response templates"
      endpoint={`${RAG_API}/response-templates/`}
      createLabel="+ Template"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'content', label: 'Content', type: 'textarea', required: true },
      ]}
    />
  );
}

export function RagDocumentTemplates() {
  return (
    <CrudPage
      {...P} helpPage="doc_templates" icon="📑" title="Document templates"
      endpoint={`${RAG_API}/document-templates/`}
      createLabel="+ Template"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'content', label: 'Content', type: 'textarea' },
      ]}
    />
  );
}

export function RagFeedback() {
  return (
    <CrudPage
      {...P} helpPage="chats" icon="⭐" title="Feedback"
      endpoint={`${RAG_API}/feedback/`}
      readOnly
      fields={[{ key: 'rating', label: 'Rating' }]}
    />
  );
}

export function RagNotes() {
  return (
    <CrudPage
      {...P} helpPage="conversations" icon="🗒" title="Conversation notes"
      endpoint={`${RAG_API}/notes/`}
      createLabel="+ Note"
      fields={[
        { key: 'content', label: 'Note', type: 'textarea', required: true },
      ]}
    />
  );
}

export function RagShopify() {
  return (
    <UniPageShell
      {...P} icon="🛍" title="Shopify" helpPage="shopify"
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      <p className="uni-help__copy">
        Point Shopify&apos;s order webhook at
        {' '}<code>/api/rag/v1/shopify/order-webhook/</code>
        {' '}or the alias <code>/rag/shopify/order-webhook/</code>.
        Store the HMAC secret under RAG → Configuration (Shopify / store).
      </p>
    </UniPageShell>
  );
}

export function RagTeam() {
  const { raw } = useUniList(`${RAG_API}/conversation-status/`);
  return (
    <UniPageShell
      {...P} icon="👥" title="Team load" helpPage="team"
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      <pre className="uni-note" style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(raw, null, 2)}</pre>
    </UniPageShell>
  );
}

export function RagHooks() {
  return <HookTester {...P} helpPage="hooks" icon="🔌" title="RAG hooks" />;
}

export function RagHelp() {
  return <HelpIndexPage {...P} helpPage="help" icon="📘" title="RAG help centre" />;
}

export function RagFeatures() {
  return <FeaturesPage {...P} helpPage="features" icon="✨" title="RAG features" />;
}

export function RagAutomation() {
  const { raw, loading } = useUniList(`${RAG_API}/automation/status/`);
  return (
    <UniPageShell
      {...P} icon="⏱" title="RAG cron jobs" helpPage="dashboard"
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      <UniTable
        rows={raw?.jobs || []} loading={loading} rowKey="task"
        empty="No scheduled jobs"
        columns={[
          { key: 'schedule', label: 'Schedule' },
          { key: 'task', label: 'Task' },
        ]}
      />
    </UniPageShell>
  );
}
